/**
 * Admin i18n. English is the admin default (Docs/06-DESIGN-SYSTEM.md §10) with
 * Norwegian Bokmål available. Every display string in this app goes through
 * `t()` — there are no literals in JSX.
 *
 * When `packages/i18n` lands, the resource bundles below move there and this
 * file keeps only the admin namespace registration.
 */
import { brandInterpolation } from '@avexa/config/brand'
import i18next, { type TFunction } from 'i18next'
import { initReactI18next, useTranslation } from 'react-i18next'

import en from './locales/en.json'
import nb from './locales/nb.json'

export const SUPPORTED_LOCALES = ['en', 'nb'] as const
export type Locale = (typeof SUPPORTED_LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'en'
const LOCALE_STORAGE_KEY = 'avexa.admin.locale'

function readStoredLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY)
    if (stored && (SUPPORTED_LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale
    }
  } catch {
    // Storage can be unavailable (private mode, locked-down browser).
  }
  return DEFAULT_LOCALE
}

void i18next.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    nb: { translation: nb },
  },
  lng: readStoredLocale(),
  fallbackLng: DEFAULT_LOCALE,
  defaultNS: 'translation',
  interpolation: {
    escapeValue: false,
    /*
     * ⚠️ THE ADMIN PANEL WAS THE "FOURTH SURFACE" `brand.ts` WARNS ABOUT.
     *
     * Its header says: "`defaultVariables` is per-i18next-instance, not global.
     * A fourth surface that calls `i18next.init()` of its own must wire it too,
     * or its `{{product}}` renders as the literal text `{{product}}` on screen."
     *
     * This app does call `init()` of its own, and until 6 Oct 2026 it did not
     * wire this — so its catalogues could not use the placeholder at all and
     * carried the product's name as a hard-coded literal instead. That is why
     * the rename to Proffplassen reached the website and the mobile app and
     * stopped at the panel's own title bar.
     *
     * It supplies `{{product}}`, `{{productUpper}}`, `{{productFull}}` and
     * `{{company}}` to every `t()` call without a single call site passing them.
     * ⚠️ `{{company}}` is "Avexa Solutions" and does NOT change with the rename.
     */
    defaultVariables: brandInterpolation,
  },
  returnNull: false,
  // A missing NB value falls back to EN and is reported so the Translation
  // Manager (A-16) can surface the gap instead of it silently shipping.
  saveMissing: import.meta.env.DEV,
  missingKeyHandler: (lngs, _ns, key) => {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] missing key "${key}" for ${lngs.join(', ')}`)
    }
  },
})

/** Imperative translate, for use outside React (validators, formatters, meta). */
export const t: TFunction = i18next.t.bind(i18next)

/** Reactive translate — re-renders when the locale changes. */
export function useT(): TFunction {
  return useTranslation().t
}

export function setLocale(locale: Locale): void {
  void i18next.changeLanguage(locale)
  document.documentElement.lang = locale
  try {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale)
  } catch {
    // Non-fatal: the locale simply will not persist.
  }
}

export function getLocale(): Locale {
  return (i18next.resolvedLanguage as Locale | undefined) ?? DEFAULT_LOCALE
}

document.documentElement.lang = getLocale()

export default i18next
